<template>
  <div class="container-fluid pd-page">
    <!-- Filtros -->
    <div class="pd-filtros mb-4">
      <div class="row align-items-end">
        <div class="col-md-3 col-12 mb-2 mb-md-0">
          <label class="pd-label">Setor</label>
          <v-select
            v-model="filtro.setor_id"
            :options="setores"
            label="nome"
            :reduce="(s) => s.id"
            placeholder="Todos os setores"
            @input="buscar"
          />
        </div>
        <div class="col-md-3 col-12 mb-2 mb-md-0">
          <label class="pd-label">Classificação de risco</label>
          <select
            class="form-control pd-select"
            v-model="filtro.classificacao_id"
            @change="buscar"
          >
            <option value="">Todas</option>
            <option v-for="c in classificacoes" :key="c.id" :value="c.id">
              {{ c.nome }}
            </option>
          </select>
        </div>
        <div class="col-md-3 col-12 mb-2 mb-md-0">
          <label class="pd-label">Dia da semana</label>
          <select
            class="form-control pd-select"
            v-model="filtro.dia_semana"
            @change="buscar"
          >
            <option v-for="d in diasSemana" :key="d.valor" :value="d.valor">
              {{ d.label }}
            </option>
          </select>
        </div>
        <div class="col-md-3 col-12 mb-2 mb-md-0">
          <label class="pd-label">Janela histórica</label>
          <select
            class="form-control pd-select"
            v-model="filtro.semanas"
            @change="buscar"
          >
            <option :value="4">4 semanas</option>
            <option :value="8">8 semanas</option>
            <option :value="12">12 semanas</option>
            <option :value="26">26 semanas (~6 meses)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- KPIs -->
    <div class="row mb-4" v-if="!carregando && !semDados">
      <div class="col-md-4 col-12 mb-3 mb-md-0">
        <div class="pd-kpi pd-kpi--blue">
          <div class="pd-kpi__icon"><i class="fas fa-ticket-alt"></i></div>
          <div class="pd-kpi__body">
            <div class="pd-kpi__value">{{ totalPrevistoDia }}</div>
            <div class="pd-kpi__label">Senhas esperadas no dia</div>
          </div>
        </div>
      </div>
      <div class="col-md-4 col-12 mb-3 mb-md-0">
        <div
          class="pd-kpi"
          :class="picoJaPassou ? 'pd-kpi--green' : 'pd-kpi--orange'"
        >
          <div class="pd-kpi__icon"><i class="fas fa-clock"></i></div>
          <div class="pd-kpi__body">
            <div class="pd-kpi__value">
              {{
                pico.hora !== null
                  ? pico.hora + "h – " + (pico.hora + 1) + "h"
                  : "—"
              }}
            </div>
            <div class="pd-kpi__label">Horário de pico esperado</div>
          </div>
        </div>
      </div>
      <div class="col-md-4 col-12">
        <div class="pd-kpi pd-kpi--red">
          <div class="pd-kpi__icon"><i class="fas fa-fire"></i></div>
          <div class="pd-kpi__body">
            <div class="pd-kpi__value">{{ pico.media || "—" }}</div>
            <div class="pd-kpi__label">Média histórica no horário de pico</div>
            <div
              v-if="exibirHoje && pico.hora !== null"
              class="pd-pico-status mt-1"
            >
              <!-- Horário ainda não chegou -->
              <span v-if="!picoJaPassou" class="pd-pico-pending">
                <i class="fas fa-hourglass-half"></i>
                Previsto para as {{ pico.hora }}h
              </span>
              <!-- Horário passou e o volume foi atingido -->
              <span v-else-if="picoAtingido" class="pd-pico-ok">
                <i class="fas fa-check"></i>
                Pico atingido — hoje: {{ hojeNoPico }}
              </span>
              <!-- Horário passou mas o volume ficou abaixo -->
              <span v-else class="pd-pico-below">
                <i class="fas fa-arrow-down"></i>
                Não atingido — hoje:
                {{ hojeNoPico !== undefined ? hojeNoPico : "—" }} / esperado:
                {{ pico.media }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gráfico -->
    <div class="card pd-card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 class="pd-card__title mb-0">{{ tituloGrafico }}</h6>
            <div v-if="tagsFiltroAtivos.length" class="pd-tags mt-1">
              <span
                v-for="tag in tagsFiltroAtivos"
                :key="tag.key"
                class="pd-tag"
              >
                {{ tag.label }}
              </span>
            </div>
            <div v-if="filtro.classificacao_id" class="pd-cls-note mt-1">
              <i class="fas fa-info-circle mr-1"></i>
              Conta atendimentos com esta classificação registrada no setor
              selecionado — não representa o total geral.
            </div>
          </div>
          <div class="d-flex align-items-center" style="gap:8px">
            <span v-if="exibirHoje" class="pd-live-badge">
              <span class="pd-live-dot"></span> Tempo real
            </span>
            <span class="pd-ai-badge" title="Previsões calculadas com base no histórico de atendimentos">
              <i class="fas fa-robot"></i> Gerado por IA
            </span>
          </div>
        </div>

        <div v-if="carregando" class="pd-empty">
          <i class="fas fa-spinner fa-spin fa-2x text-primary mb-2"></i>
          <div>Carregando dados...</div>
        </div>
        <div v-else-if="semDados" class="pd-empty">
          <i class="fas fa-chart-bar fa-3x mb-3" style="color: #dee2e6"></i>
          <div class="font-weight-bold mb-1">Nenhum histórico disponível</div>
          <small class="text-muted"
            >O sistema precisa de pelo menos 1 semana de uso para gerar
            previsões.</small
          >
        </div>
        <div v-else style="position: relative; height: 380px">
          <bar-chart :chart-data="chartData" :chart-options="chartOptions" />
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div class="card pd-card" v-if="!carregando && !semDados">
      <div class="card-body p-0">
        <div class="pd-table-header px-3 py-2">
          <h6 class="pd-card__title mb-0">Detalhamento por hora</h6>
        </div>
        <table class="table table-sm mb-0 pd-table">
          <thead>
            <tr>
              <th>Hora</th>
              <th class="text-center">Média histórica</th>
              <th class="text-center" v-if="exibirHoje">Hoje (real)</th>
              <th class="text-center" v-if="exibirHoje">Variação</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="hora in horasComDados"
              :key="hora"
              :class="{ 'pd-table__row--pico': hora === pico.hora }"
            >
              <td>
                <span class="pd-hora"
                  >{{ String(hora).padStart(2, "0") }}:00</span
                >
                <i
                  v-if="hora === pico.hora"
                  class="fas fa-fire ml-2 text-warning"
                  title="Horário de pico"
                ></i>
              </td>
              <td class="text-center">
                <div class="pd-bar-inline">
                  <div
                    class="pd-bar-inline__fill"
                    :style="{ width: barWidth(hora) + '%' }"
                  ></div>
                  <span class="pd-bar-inline__val">{{
                    mediaHistorico[hora] || 0
                  }}</span>
                </div>
              </td>
              <td class="text-center" v-if="exibirHoje">
                <span
                  v-if="hoje[hora] !== undefined"
                  class="font-weight-bold"
                  >{{ hoje[hora] }}</span
                >
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center" v-if="exibirHoje">
                <template
                  v-if="hoje[hora] !== undefined && mediaHistorico[hora]"
                >
                  <span
                    class="pd-badge"
                    :class="
                      variacao(hora) > 0 ? 'pd-badge--up' : 'pd-badge--down'
                    "
                  >
                    <i
                      :class="
                        variacao(hora) > 0
                          ? 'fas fa-arrow-up'
                          : 'fas fa-arrow-down'
                      "
                    ></i>
                    {{ Math.abs(variacao(hora)) }}%
                  </span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import BarChart from "@/components/BarChart.vue";

const DIAS_SEMANA = [
  { valor: 1, label: "Domingo" },
  { valor: 2, label: "Segunda-feira" },
  { valor: 3, label: "Terça-feira" },
  { valor: 4, label: "Quarta-feira" },
  { valor: 5, label: "Quinta-feira" },
  { valor: 6, label: "Sexta-feira" },
  { valor: 7, label: "Sábado" },
];

function diaSemanaMySQL() {
  return new Date().getDay() + 1;
}

export default {
  name: "PrevisaoDemanda",
  components: { vSelect, BarChart },

  data() {
    return {
      carregando: false,
      setores: [],
      classificacoes: [],
      semanas_base: 12,
      mediaHistorico: {},
      hoje: {},
      pico: { hora: null, media: null },
      filtro: {
        setor_id: null,
        classificacao_id: "",
        semanas: 12,
        dia_semana: diaSemanaMySQL(),
      },
      diasSemana: DIAS_SEMANA,
      hojeHoraAtual: new Date().getHours(),
      diaSemanaHoje: diaSemanaMySQL(),
    };
  },

  computed: {
    exibirHoje() {
      return this.filtro.dia_semana === this.diaSemanaHoje;
    },
    tituloGrafico() {
      const diaLabel =
        (DIAS_SEMANA.find((d) => d.valor === this.filtro.dia_semana) || {})
          .label || "";
      return this.exibirHoje
        ? "Média histórica × Hoje (em tempo real)"
        : `Média histórica — ${diaLabel}`;
    },
    tagsFiltroAtivos() {
      const tags = [];
      if (this.filtro.classificacao_id) {
        const cls = this.classificacoes.find(
          (c) => c.id == this.filtro.classificacao_id
        );
        if (cls) tags.push({ key: "cls", label: cls.nome });
      }
      if (this.filtro.setor_id) {
        const s = this.setores.find((s) => s.id === this.filtro.setor_id);
        if (s) tags.push({ key: "setor", label: s.nome });
      }
      if (this.filtro.semanas !== 12) {
        tags.push({ key: "sem", label: `${this.filtro.semanas} semanas` });
      }
      return tags;
    },
    horasComDados() {
      const horas = new Set(Object.keys(this.mediaHistorico).map(Number));
      if (this.exibirHoje) {
        Object.keys(this.hoje).forEach((h) => horas.add(Number(h)));
      }
      return Array.from(horas).sort((a, b) => a - b);
    },
    semDados() {
      return Object.keys(this.mediaHistorico).length === 0;
    },
    totalPrevistoDia() {
      return Object.values(this.mediaHistorico).reduce((a, b) => a + b, 0);
    },
    picoJaPassou() {
      return (
        this.exibirHoje &&
        this.pico.hora !== null &&
        this.hojeHoraAtual > this.pico.hora
      );
    },
    hojeNoPico() {
      return this.pico.hora !== null ? this.hoje[this.pico.hora] : undefined;
    },
    picoAtingido() {
      return (
        this.picoJaPassou &&
        this.hojeNoPico !== undefined &&
        this.hojeNoPico >= this.pico.media
      );
    },
    maxMedia() {
      return Math.max(1, ...Object.values(this.mediaHistorico));
    },
    chartData() {
      const labels = Array.from({ length: 24 }, (_, i) => `${i}h`);
      const mediaValues = labels.map((_, i) => this.mediaHistorico[i] ?? 0);
      const hojeValues = labels.map((_, i) =>
        this.hoje[i] !== undefined ? this.hoje[i] : null
      );

      const datasets = [
        {
          label: "Média histórica",
          data: mediaValues,
          backgroundColor: "rgba(54, 120, 200, 0.5)",
          borderColor: "rgba(54, 120, 200, 0.8)",
          borderWidth: 1,
        },
      ];

      if (this.exibirHoje) {
        datasets.push({
          label: "Hoje (real)",
          data: hojeValues,
          backgroundColor: "rgba(255, 140, 0, 0.7)",
          borderColor: "rgba(255, 140, 0, 1)",
          borderWidth: 1,
        });
      }

      return { labels, datasets };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: "top" },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
          xAxes: [{ gridLines: { display: false } }],
        },
        tooltips: {
          callbacks: {
            label(item, data) {
              const label = data.datasets[item.datasetIndex].label;
              const val = item.yLabel;
              return val !== null ? `${label}: ${val} senhas` : null;
            },
          },
        },
      };
    },
  },

  methods: {
    buscar() {
      const self = this;
      self.carregando = true;

      const api = self.$store.state.api + "previsao-demanda";
      const params = {
        dia_semana: self.filtro.dia_semana,
        semanas: self.filtro.semanas,
      };
      if (self.filtro.setor_id) params.setor_id = self.filtro.setor_id;
      if (self.filtro.classificacao_id)
        params.classificacao_id = self.filtro.classificacao_id;

      axios
        .get(api, { params })
        .then((response) => {
          const data = response.data;
          self.mediaHistorico = data.media_por_hora || {};
          self.hoje = data.hoje || {};
          self.pico = data.pico || { hora: null, media: null };
          self.semanas_base = data.semanas_base || 12;
          if (data.setores && data.setores.length) {
            self.setores = data.setores;
          }
          if (data.classificacoes && data.classificacoes.length) {
            self.classificacoes = data.classificacoes;
          }
          self.carregando = false;
        })
        .catch((error) => {
          self.carregando = false;
          self.$message(
            "Erro",
            (error.response && error.response.data) ||
              "Não foi possível carregar a previsão.",
            "error"
          );
        });
    },
    variacao(hora) {
      const media = this.mediaHistorico[hora] || 0;
      const real = this.hoje[hora] || 0;
      if (!media) return 0;
      return Math.round(((real - media) / media) * 100);
    },
    barWidth(hora) {
      return Math.round(
        ((this.mediaHistorico[hora] || 0) / this.maxMedia) * 100
      );
    },
  },

  mounted() {
    this.buscar();
  },
};
</script>

<style scoped>
.pd-page {
  background: #f4f6f9;
  min-height: 100vh;
  padding-top: 8px;
}

/* Filtros */
.pd-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8a94a6;
  display: block;
  margin-bottom: 6px;
}
.pd-select {
  border-radius: 8px;
  border: 1px solid #e0e6ed;
  font-size: 14px;
  height: 38px;
}

/* Status do pico */
.pd-pico-status {
  display: flex;
}
.pd-pico-ok,
.pd-pico-pending {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
}
.pd-pico-ok {
  background: #ecfdf3;
  color: #12b76a;
}
.pd-pico-pending {
  background: #fff4eb;
  color: #ff9d3d;
}
.pd-pico-below {
  background: #f0f4ff;
  color: #4f6ef7;
}

/* Nota informativa classificação */
.pd-cls-note {
  font-size: 11px;
  color: #8a94a6;
}

/* Tags de filtros ativos */
.pd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pd-tag {
  background: #eef2ff;
  color: #4f6ef7;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
}

/* KPIs */
.pd-kpi {
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-left: 5px solid #e0e6ed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 100%;
}
.pd-kpi--blue {
  border-color: #4f6ef7;
}
.pd-kpi--orange {
  border-color: #ff9d3d;
}
.pd-kpi--red {
  border-color: #f04438;
}
.pd-kpi--green {
  border-color: #12b76a;
}

.pd-kpi__icon {
  font-size: 22px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f4f6f9;
  color: #8a94a6;
}
.pd-kpi--blue .pd-kpi__icon {
  background: #eef2ff;
  color: #4f6ef7;
}
.pd-kpi--orange .pd-kpi__icon {
  background: #fff4eb;
  color: #ff9d3d;
}
.pd-kpi--red .pd-kpi__icon {
  background: #fef3f2;
  color: #f04438;
}
.pd-kpi--green .pd-kpi__icon {
  background: #ecfdf3;
  color: #12b76a;
}

.pd-kpi__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a2233;
}
.pd-kpi__label {
  font-size: 12px;
  color: #8a94a6;
  margin-top: 3px;
}

/* Card */
.pd-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.pd-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #1a2233;
}

/* Badge ao vivo */
.pd-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ecfdf3;
  color: #12b76a;
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
}
.pd-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #12b76a;
  animation: pulse 1.5s infinite;
}

/* Badge IA */
.pd-ai-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f0edff;
  color: #7c3aed;
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

/* Estado vazio */
.pd-empty {
  text-align: center;
  padding: 60px 20px;
  color: #8a94a6;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Tabela */
.pd-table-header {
  border-bottom: 1px solid #f0f2f5;
}
.pd-table thead th {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #8a94a6;
  border-top: none;
  border-bottom: 1px solid #f0f2f5;
  padding: 10px 16px;
}
.pd-table tbody td {
  padding: 10px 16px;
  vertical-align: middle;
  border-color: #f0f2f5;
  font-size: 13px;
}
.pd-table__row--pico {
  background: #f8ebba !important;
}
.pd-hora {
  font-weight: 600;
  color: #1a2233;
  font-variant-numeric: tabular-nums;
}

/* Mini bar inline */
.pd-bar-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pd-bar-inline__fill {
  height: 6px;
  border-radius: 3px;
  background: #4f6ef7;
  min-width: 2px;
  flex-shrink: 0;
}
.pd-bar-inline__val {
  font-size: 13px;
  font-weight: 500;
  color: #1a2233;
  white-space: nowrap;
}

/* Badge variação */
.pd-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}
.pd-badge--up {
  background: #fef3f2;
  color: #f04438;
}
.pd-badge--down {
  background: #ecfdf3;
  color: #12b76a;
}
</style>

<style>
/* ── Dark mode ── */
.dark-layout .pd-page {
  background: #161d31;
}

.dark-layout .pd-label {
  color: #676d7d;
}

.dark-layout .pd-select {
  background: #283046;
  border-color: #3b4253;
  color: #d0d2d6;
}

.dark-layout .pd-kpi {
  background: #283046;
  border-left-color: #3b4253;
  box-shadow: none;
}
.dark-layout .pd-kpi--blue {
  border-left-color: #4f6ef7;
}
.dark-layout .pd-kpi--orange {
  border-left-color: #ff9d3d;
}
.dark-layout .pd-kpi--red {
  border-left-color: #f04438;
}
.dark-layout .pd-kpi--green {
  border-left-color: #12b76a;
}

.dark-layout .pd-kpi__icon {
  background: #1e2a3a;
}
.dark-layout .pd-kpi--blue .pd-kpi__icon {
  background: rgba(79, 110, 247, 0.15);
  color: #4f6ef7;
}
.dark-layout .pd-kpi--orange .pd-kpi__icon {
  background: rgba(255, 157, 61, 0.15);
  color: #ff9d3d;
}
.dark-layout .pd-kpi--red .pd-kpi__icon {
  background: rgba(240, 68, 56, 0.15);
  color: #f04438;
}
.dark-layout .pd-kpi--green .pd-kpi__icon {
  background: rgba(18, 183, 106, 0.15);
  color: #12b76a;
}

.dark-layout .pd-kpi__value {
  color: #d0d2d6;
}
.dark-layout .pd-kpi__label {
  color: #676d7d;
}

.dark-layout .pd-pico-ok {
  background: rgba(18, 183, 106, 0.15);
  color: #12b76a;
}
.dark-layout .pd-pico-pending {
  background: rgba(255, 157, 61, 0.15);
  color: #ff9d3d;
}
.dark-layout .pd-pico-below {
  background: rgba(79, 110, 247, 0.15);
  color: #7e9cf7;
}

.dark-layout .pd-card {
  background: #283046;
  box-shadow: none;
}
.dark-layout .pd-card__title {
  color: #d0d2d6;
}

.dark-layout .pd-tag {
  background: rgba(79, 110, 247, 0.15);
  color: #7e9cf7;
}

.dark-layout .pd-cls-note {
  color: #676d7d;
}

.dark-layout .pd-live-badge {
  background: rgba(18, 183, 106, 0.15);
  color: #12b76a;
}
.dark-layout .pd-live-dot {
  background: #12b76a;
}
.dark-layout .pd-ai-badge {
  background: rgba(124, 58, 237, 0.15);
  color: #a78bfa;
}

.dark-layout .pd-empty {
  color: #676d7d;
}

.dark-layout .pd-table-header {
  border-color: #3b4253;
}

.dark-layout .pd-table thead th {
  color: #676d7d;
  border-color: #3b4253;
}

.dark-layout .pd-table tbody td {
  border-color: #3b4253;
  color: #d0d2d6;
}

.dark-layout .pd-table__row--pico {
  background: rgba(255, 193, 7, 0.08) !important;
}

.dark-layout .pd-hora {
  color: #d0d2d6;
}

.dark-layout .pd-bar-inline__fill {
  background: #4f6ef7;
}
.dark-layout .pd-bar-inline__val {
  color: #d0d2d6;
}

.dark-layout .pd-badge--up {
  background: rgba(240, 68, 56, 0.15);
  color: #f97066;
}
.dark-layout .pd-badge--down {
  background: rgba(18, 183, 106, 0.15);
  color: #12b76a;
}
</style>
